import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Users, Filter, AlertCircle, Loader2, X } from 'lucide-react';
import EmployeeModal from './EmployeeModal.tsx';
import DeleteConfirmation from './DeleteConfirmation.tsx';
import { Employee } from '../../types/employee.ts';
import { apiService } from '../../../service/api.ts';
import { mapApiEmployeeToEmployee } from '../../utils/employeeMapper.ts';

const UserManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterAssociateType, setFilterAssociateType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Load employees from API
  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiEmployees = await apiService.getUserList();
      const mappedEmployees = apiEmployees.map(mapApiEmployeeToEmployee);
      setEmployees(mappedEmployees);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load employees');
      console.error('Error loading employees:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const handleCreateEmployee = async (employeeData: Omit<Employee, 'id' | 'createdAt'>) => {
    try {
      setActionLoading('create');
      const createRequest = {
        name: employeeData.name,
        email: employeeData.email,
        role: employeeData.role,
        associate_type: employeeData.associateType,
        created_by: employeeData.createdBy,
      };
      
      const newApiEmployee = await apiService.createUser(createRequest);
      const newEmployee = mapApiEmployeeToEmployee(newApiEmployee);
      loadEmployees()
      setEmployees([...employees, newEmployee]);
      setIsModalOpen(false);
    } catch (err) {
      // setError(err instanceof Error ? err.message : 'Failed to create employee');
      // console.error('Error creating employee:', err);
      setIsModalOpen(false);
      loadEmployees()

    } finally {
      setActionLoading(null);
    }
  };

  const handleEditEmployee = async (employeeData: Omit<Employee, 'id' | 'createdAt'>) => {
    if (!editingEmployee) return;
    
    try {
      setActionLoading('edit');
      const updateRequest = {
        name: employeeData.name,
        email: employeeData.email,
        role: employeeData.role,
        associate_type: employeeData.associateType,
        created_by: employeeData.createdBy,
      };
      
      const updatedApiEmployee = await apiService.updateUser(parseInt(editingEmployee.id), updateRequest);
      const updatedEmployee = mapApiEmployeeToEmployee(updatedApiEmployee);
      loadEmployees()
      setEmployees(employees.map(emp => emp.id === editingEmployee.id ? updatedEmployee : emp));
      setEditingEmployee(null);
    } catch (err) {
      // setError(err instanceof Error ? err.message : 'Failed to update employee');
      // console.error('Error updating employee:', err);
      setEditingEmployee(null);
      loadEmployees()
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteEmployee = async () => {
    if (!deletingEmployee) return;
    
    try {
      setActionLoading('delete');
      await apiService.deleteUser(parseInt(deletingEmployee.id));
      setEmployees(employees.filter(emp => emp.id !== deletingEmployee.id));
      setDeletingEmployee(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete employee');
      console.error('Error deleting employee:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === '' || employee.role === filterRole;
    const matchesAssociateType = filterAssociateType === '' || employee.associateType === filterAssociateType;
    
    return matchesSearch && matchesRole && matchesAssociateType;
  });

  const roles = [...new Set(employees.map(emp => emp.role))];
  const associateTypes = [...new Set(employees.map(emp => emp.associateType))];

  const dismissError = () => setError(null);

  if (loading) {
    return (
      <div
        role="status"
        style={{
          position: "absolute",
          left: "0px",
          right: "0px",
          top: "0px",
          bottom: "0px",
          background: "rgba(0,0,0,0.8)",
          zIndex: "99999",
          display: "grid",
          alignContent:"center"
          ,justifyContent: "center",
          justifyItems: "center",
          alignItems: "center",
        }}
      >
        <svg
          aria-hidden="true"
          className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-purple-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="pt-4 text-white">Loading Admin Dashboad, Please wait...</span>

      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-red-800">{error}</p>
            </div>
            <button
              onClick={dismissError}
              className="text-red-600 hover:text-red-800 p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Employee Management</h1>
                <p className="text-gray-600">Manage your team members and their information</p>
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              disabled={actionLoading === 'create'}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md"
            >
              {actionLoading === 'create' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Plus className="h-5 w-5" />
              )}
              <span>Add Employee</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Employees</p>
                <p className="text-3xl font-bold text-gray-900">{employees.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Full-time</p>
                <p className="text-3xl font-bold text-gray-900">
                  {employees.filter(emp => emp.associateType === 'Full-time').length}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Contract</p>
                <p className="text-3xl font-bold text-gray-900">
                  {employees.filter(emp => emp.associateType === 'Contract').length}
                </p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            <div className="flex items-center space-x-3">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">All Roles</option>
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <select
                value={filterAssociateType}
                onChange={(e) => setFilterAssociateType(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">All Types</option>
                {associateTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Employee Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Associate Type</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-sm">
                            {employee.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{employee.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {employee.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        employee.associateType === 'Full-time' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {employee.associateType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{employee.createdBy}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {employee.createdAt.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingEmployee(employee)}
                          disabled={actionLoading === 'edit'}
                          className="text-blue-600 hover:text-blue-900 disabled:text-blue-400 p-2 hover:bg-blue-50 rounded-lg transition-all duration-150"
                        >
                          {actionLoading === 'edit' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Edit className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setDeletingEmployee(employee)}
                          disabled={actionLoading === 'delete'}
                          className="text-red-600 hover:text-red-900 disabled:text-red-400 p-2 hover:bg-red-50 rounded-lg transition-all duration-150"
                        >
                          {actionLoading === 'delete' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredEmployees.length === 0 && !loading && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No employees found</p>
              <p className="text-gray-400">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {(isModalOpen || editingEmployee) && (
        <EmployeeModal
          isOpen={isModalOpen || !!editingEmployee}
          onClose={() => {
            setIsModalOpen(false);
            setEditingEmployee(null);
          }}
          onSubmit={editingEmployee ? handleEditEmployee : handleCreateEmployee}
          employee={editingEmployee}
          title={editingEmployee ? 'Edit Employee' : 'Add New Employee'}
          loading={actionLoading === 'create' || actionLoading === 'edit'}
        />
      )}

      {deletingEmployee && (
        <DeleteConfirmation
          isOpen={!!deletingEmployee}
          onClose={() => setDeletingEmployee(null)}
          onConfirm={handleDeleteEmployee}
          employeeName={deletingEmployee.name}
          loading={actionLoading === 'delete'}
        />
      )}
    </div>
  );
};

export default UserManagement;